import { CategoryNameHr, HrCountryId, ModuleNameHr, PageRoute, RealEstatePricesUserTypeValues } from "../../models";
import { api } from "../../services";
import {
  LocationSearchResultI,
  ResolvedLocationI
} from "../../typings/configurationTypes";
import { buildLocationRoutePath } from "../../routes/Configuration/helpers";

// URL helpers for real estate prices pages
export const PRICES_BASE_PATH = "/" + ModuleNameHr.realEstate + PageRoute.realEstatePrices;

export const buildPricesResultsUrl = (propertyType: string, locationRoute: string): string => {
  return PRICES_BASE_PATH + "/" + propertyType + "/" + locationRoute + "?userType=" + RealEstatePricesUserTypeValues.ALL_USERS;
};

export interface LocationSlugMatch {
  countyId?: string;
  cityId?: string;
  settlementId?: string;
  name: string;
  countyName?: string;
  cityName?: string;
  settlementName?: string;
}

// A missing location is a valid answer (404), not something to toast.
const ignoreError = () => null;

// Resolve a hierarchical location path (county > city > settlement slugs) into
// IDs and display names through configuration/locations/resolve. Caller passes
// whatever number of slugs are present in the URL (1 = county, 2 = county+city,
// 3 = county+city+settlement). The whole chain is validated by the API.
export const resolveLocationByPath = async (
  countySlug: string,
  citySlug?: string,
  settlementSlug?: string
): Promise<LocationSlugMatch | null> => {
  if (!countySlug) return null;

  const resolved: ResolvedLocationI | null = await api.Configuration.resolveLocation(
    {
      county: countySlug,
      city: citySlug || undefined,
      settlement: settlementSlug || undefined
    },
    ignoreError
  );

  return resolvedToSlugMatch(resolved);
};

export const resolvedToSlugMatch = (
  resolved: ResolvedLocationI | null | undefined
): LocationSlugMatch | null => {
  if (!resolved || !resolved.countyId) return null;

  return {
    countyId: resolved.countyId,
    cityId: resolved.cityId || undefined,
    settlementId: resolved.settlementId || undefined,
    name: resolved.name,
    countyName: resolved.countyName || undefined,
    cityName: resolved.cityName || undefined,
    settlementName: resolved.settlementName || undefined
  };
};

export interface PropertyTypeOption {
  slug: string;
  label: string;
}

export interface CategoryLike {
  moduleName?: string;
  name?: string;
  route?: string;
  displayName?: string;
}

// A real-estate "for sale" category is supported by the prices feature.
// BE drives the list — labels come from `displayName` — but the display order
// is spec-defined (most relevant first).
const isSupportedSaleCategory = (c: CategoryLike): boolean => {
  return Boolean(c && c.moduleName === "real-estate" && (c.name || "").endsWith("-for-sale"));
};

const PROPERTY_TYPE_DISPLAY_ORDER: string[] = [
  CategoryNameHr.flatsForSale,
  CategoryNameHr.housesForSale,
  CategoryNameHr.landsForSale,
  CategoryNameHr.businessSpacesForSale,
  CategoryNameHr.holidayPropertiesForSale,
  CategoryNameHr.garagesForSale
];

export const buildPropertyTypeOptions = (
  categories: CategoryLike[] | undefined
): PropertyTypeOption[] => {
  const orderIndex = (slug: string): number => {
    const i = PROPERTY_TYPE_DISPLAY_ORDER.indexOf(slug);
    return i === -1 ? PROPERTY_TYPE_DISPLAY_ORDER.length : i;
  };
  return (categories || [])
    .filter(c => isSupportedSaleCategory(c) && c.route)
    .map(c => ({
      slug: c.route || "",
      label: c.displayName || c.route || ""
    }))
    .sort((a, b) => orderIndex(a.slug) - orderIndex(b.slug));
};

export const findPropertyType = (
  slug: string | null | undefined,
  categories: CategoryLike[] | undefined
): PropertyTypeOption | null => {
  if (!slug) return null;
  const category = (categories || []).find(c => c.route === slug && isSupportedSaleCategory(c));
  if (!category) return null;
  return { slug: slug, label: category.displayName || slug };
};

export const isPropertyTypeSlug = (
  slug: string | null | undefined,
  categories: CategoryLike[] | undefined
): boolean => {
  return Boolean(findPropertyType(slug, categories));
};

// Location option for Select dropdowns
export interface LocationOption {
  key: string;
  label: string;
  countryId: string;
  countyId: string;
  cityId: string | null;
  settlementId: string | null;
  route: string;
}

const locationOptionKey = (level: number, id: string) =>
  `${level === 1 ? "county" : level === 2 ? "city" : "settlement"}:${id}`;

// Option from one hit of configuration/locations/search
export const locationOptionFromSearchResult = (
  result: LocationSearchResultI
): LocationOption => ({
  key: locationOptionKey(result.level, result.id),
  label: [result.countyName, result.cityName, result.name].filter(Boolean).join(" > "),
  countryId: HrCountryId,
  countyId: result.level === 1 ? result.id : result.countyId,
  cityId: result.level === 2 ? result.id : result.cityId,
  settlementId: result.level === 3 ? result.id : null,
  route: buildLocationRoutePath(result)
});

// Option from configuration/locations/resolve
export const locationOptionFromResolved = (
  resolved: ResolvedLocationI | null | undefined
): LocationOption | null => {
  if (!resolved || !resolved.countyId) return null;

  return {
    key: locationOptionKey(resolved.level, resolved.id),
    label: [resolved.countyName, resolved.cityName, resolved.settlementName]
      .filter(Boolean)
      .join(" > "),
    countryId: HrCountryId,
    countyId: resolved.countyId,
    cityId: resolved.cityId || null,
    settlementId: resolved.settlementId || null,
    route: buildLocationRoutePath(resolved)
  };
};

// Option for a county entry of configuration/locations/:id/children
export const locationOptionFromCounty = (county: {
  id: string;
  name: string;
  route: string | null;
}): LocationOption => ({
  key: locationOptionKey(1, county.id),
  label: county.name,
  countryId: HrCountryId,
  countyId: county.id,
  cityId: null,
  settlementId: null,
  route: county.route || ""
});



// WEBPACK FOOTER //
// ./src/containers/RealEstatePricesPage/helpers.ts